import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useEditorContext } from "../context/editorContext";
import { getLayoutName, getGridConfig } from "../Helpers";
import { DataGridCustomizer } from "../../../tables";
import { useSelectedRowContext } from "./SelectedRowContext";
import { useAppQuoteContext } from "../../../../../../context/AppQuoteContext";
import { resolveOpenQuoteTabId } from "./resolveOpenQuoteTabId";

const useTabsWrapperContainer = () => {
	const {
		contentGrid,
		handleCKEditorSelectedTabId,
	} = useEditorContext();
	const {quote, quote: { Tabs }, updateQuoteTabs} = useAppQuoteContext();
	const {setSelectedRows} = useSelectedRowContext();
	const resolvedOpenTabId = resolveOpenQuoteTabId(Tabs);
	const [change, setChange] = useState(false);
	const [duplicatedTabId, setDuplicatedTabId] = useState(null);
	const [layoutChanged, setLayoutChanged] = useState(false);
	const [loading, setLoading] = useState(false);
	const [open, setOpen] = useState(() => resolveOpenQuoteTabId(Tabs));
	const [layoutsName, setLayoutsName] = useState(() => preparedStateInitial(Tabs));
	const deleteModalRef = useRef();
	const marginModalRef = useRef();
	const newRecordRef = useRef();
	const priceSourceModalRef = useRef();

	useEffect(() => {
		if (resolvedOpenTabId == null) {
			return;
		}
		setOpen((prevOpen) => {
			const prevOpenIsValid =
				prevOpen != null && Tabs.some((tab) => tab.IdQuoteTabs == prevOpen);
			if (prevOpenIsValid) {
				return prevOpen;
			}
			return resolvedOpenTabId;
		});
	}, [Tabs, resolvedOpenTabId]);

	useEffect(() => {
		if (open == null) {
			return;
		}
		const openIsValid = Tabs.some((tab) => tab.IdQuoteTabs == open);
		if (!openIsValid) {
			return;
		}
		const storedTabId = sessionStorage.getItem("cpq_open_tab");
		if (storedTabId != open) {
			sessionStorage.setItem("cpq_open_tab", open);
			handleCKEditorSelectedTabId(open);
		}
	}, [open, Tabs, handleCKEditorSelectedTabId]);

	const updateOpenTab = useCallback((arg) => {
		if (arg != null) {
			sessionStorage.setItem("cpq_open_tab", arg);
		}
		setOpen(arg);
		handleCKEditorSelectedTabId(arg);
		setSelectedRows([]);
	}, [handleCKEditorSelectedTabId, setSelectedRows]);

	const itemIds = useMemo(() => [], []);
	const isEditable = !quote.IsLocked && !app.currentUser.IsReadOnly;
	const lastTab = Tabs.length === 1;

	function preparedStateInitial(currentTabs) {
		if (!currentTabs?.length) {
			return {};
		}
		let combinedData = {};
		currentTabs.forEach((tab) => {
			const tabId = tab.IdQuoteTabs;
			const layoutName = getLayoutName(tabId);
			combinedData = { ...combinedData, [tabId]: layoutName.ConfigurationName };
		});
		return combinedData;
	}

	const confirmDeletion = useCallback(
		(itemToDelete) => {
			for (let i = 0; i < quote.Items.length; i++) {
				if (quote.Items[i].IdQuoteTabs == itemToDelete) {
					itemIds.push(quote.Items[i].IdQuoteItems);
				}
			}		

			const afterDelete = function (msg) {
				if (msg.error) {
					Dialog.open({
						title: "ERROR",
						message: msg.error,
						links: [
							{
								title: "OK",
								callback: function callback() {
									Dialog.close();
								}
							}
						]
					});
				} else {
					deleteModalRef.current.showModal(false);
				}
			};
			
			const  deleteApi = quosal.api.tab.deleteTab(itemToDelete);
			deleteApi.finished = function (msg) {
				quosal.sell.quote.updateFromApiResponse(msg);
				afterDelete(msg);

				if (open === itemToDelete && quote.Tabs.length !== 1) {
					if (open === quote.Tabs[quote.Tabs.length - 1].IdQuoteTabs) {
						updateOpenTab(quote.Tabs[0].IdQuoteTabs);
					} else {
						const index = quote.Tabs.findIndex(
							(item) => item.IdQuoteTabs === itemToDelete
						);
						updateOpenTab(quote.Tabs[index + 1].IdQuoteTabs);
					}
				} 

				Dialog.setIsWorking(false);
			};
			Dialog.setIsWorking(true);
			deleteApi.call();
		},
		[Tabs, itemIds, open, updateOpenTab]
	);

	const handleDeleteTab = (isShown, id) => {
		const retentionPeriod = app.settings.global.TabDeletionRetentionPeriod;
		const presetData = {
			lastItem: lastTab,
			titleText: lastTab ? "Unable to Delete Tab" : "Confirm Delete Tab",
			confirmationText: lastTab
				? "You can not delete the last tab on this quote."
				: "Are you sure you want to delete this tab? The tab can only be restored within " + retentionPeriod + " days after deletion.",
			deleteCallback: () => confirmDeletion(id)
		};
		deleteModalRef.current.showModal(isShown, presetData);
	};

	const handleDeleteGridRow = (isShown, rows, hasBundle, totalBundle, hasProtectedItem, deleteCallback) => {
		const plural = rows?.length > 1 ? "s" : "";
		const pluralPackage = totalBundle > 1 ? "s" : "";
		const cannotDeleteProtectedItem = hasProtectedItem && rows?.length == 0;
		const presetData = {
			lastItem: cannotDeleteProtectedItem,
			titleText: cannotDeleteProtectedItem ? 'No items selected' : 'Confirm Delete Item' + plural,
			confirmationText: (cannotDeleteProtectedItem ? 'You must select one or more unprotected items for this operation.' : 
				`Are you sure you want to delete ${rows?.length} item${plural}` + 
				(hasBundle ? ', including ' + totalBundle + ' bundle' + pluralPackage : '') + '?' +
				(hasProtectedItem ? ' Protected items will not be deleted.' : '')),
			rows,
			deleteCallback: () => deleteCallback(rows)
		};
		deleteModalRef.current.showModal(isShown, presetData);
	};

	const saveTabOrderChange = function(updatedData) {
		const updates = [];
		for (let i = 0; i < quote.Tabs.length; i++) {
			if (quote.Tabs[i].IdQuoteTabs !== updatedData[i].IdQuoteTabs) {
			const tabNumber = i + 1;
				updates.push({
					fields: {
						TabNumber: tabNumber
					},
					queries: [
						{
							table: "QuoteTabs",
							where: [
								{
									field: "IdQuoteTabs",
									operator: "Equals",
									value: updatedData[i].IdQuoteTabs
								}
							]
						}
					]
				});
			}
		}

		if (updates.length > 0) {
			const updateApi = quosal.api.data.update(updates, quote.IdQuoteMain);
			updateApi.setFlag("fixTabOrder");
			updateApi.finished = function (msg) {
				quosal.sell.quote.updateFromApiResponse(msg);
			};
			updateApi.call();
			return true;
		}
		return false;
	};

	const handleMove = useCallback(
		(sourceId, targetId) => {
			const sourceIndex = Tabs.findIndex((item) => item.id === sourceId);
			const targetIndex = Tabs.findIndex((item) => item.id === targetId);
			const updatedData = [...Tabs];

			const [removed] = updatedData.splice(sourceIndex, 1);
			updatedData.splice(targetIndex, 0, removed);
			updateQuoteTabs(()=>updatedData);
			saveTabOrderChange(updatedData);
		},
		[Tabs]
	);

	const gridCallback = useCallback(
		(LayoutName, tabId) => {
			setLayoutsName({ ...layoutsName, [tabId]: LayoutName });
			setLayoutChanged(layoutChanged);
		},
		[layoutChanged, layoutsName]
	);

	const openCustomizeGrid = useCallback(
		(tabId) => {
			const gridConfiguration = getGridConfig(tabId, layoutsName[tabId]);
			const showGridCustomizer = DataGridCustomizer.showDataGridCustomizer.bind(
				null,
				gridConfiguration,
				true,
				() => setChange(!change),
				false,
				tabId,
				() => setChange(!change),
			);
			DataGridCustomizer.executeTabsWrapperCallback = gridCallback;
			showGridCustomizer();
		},
		[change, gridCallback, layoutsName]
	);

	return {
		contentGrid,
		duplicatedTabId,
		isEditable,
		lastTab,
		loading,
		open,
		change,
		Tabs,
		deleteModalRef,
		marginModalRef,
		newRecordRef,
		priceSourceModalRef,
		updateOpenTab,
		setChange,
		setDuplicatedTabId,
		setLoading,
		handleDeleteTab,
		handleDeleteGridRow,
		handleMove,
		openCustomizeGrid
	};
};

export default useTabsWrapperContainer;
